
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface IDDocumentType {
  id: string;
  document_name: string;
  country_code: string;
  description?: string;
  proves_citizenship?: boolean;
  proves_residency?: boolean;
  global_document?: boolean;
  expatriate_identifier?: boolean;
  required?: boolean;
}

export const useIDTypes = () => {
  const queryClient = useQueryClient();
  
  // Fetch ID document types
  const query = useQuery({
    queryKey: ['id-document-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('id_document_types')
        .select('*')
        .order('country_code', { ascending: true })
        .order('document_name', { ascending: true });
        
      if (error) throw error;
      return data as IDDocumentType[];
    }
  });
  
  // Create a new ID document type
  const createIDType = useMutation({
    mutationFn: async (newType: Omit<IDDocumentType, 'id'>) => {
      const { data, error } = await supabase
        .from('id_document_types')
        .insert(newType)
        .select('*')
        .single();
        
      if (error) throw error;
      return data as IDDocumentType;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['id-document-types'] });
    }
  });
  
  // Update an existing ID document type
  const updateIDType = useMutation({
    mutationFn: async (updatedType: IDDocumentType) => {
      const { data, error } = await supabase
        .from('id_document_types')
        .update({
          document_name: updatedType.document_name,
          country_code: updatedType.country_code,
          description: updatedType.description,
          proves_citizenship: updatedType.proves_citizenship,
          proves_residency: updatedType.proves_residency,
          global_document: updatedType.global_document,
          expatriate_identifier: updatedType.expatriate_identifier,
          required: updatedType.required
        })
        .eq('id', updatedType.id)
        .select('*')
        .single();
        
      if (error) throw error;
      return data as IDDocumentType;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['id-document-types'] });
    }
  });
  
  // Delete an ID document type
  const deleteIDType = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('id_document_types')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['id-document-types'] });
    }
  });
  
  return {
    idTypes: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    createIDType,
    updateIDType,
    deleteIDType
  };
};
